import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Brain, LayoutDashboard, FolderKanban, LogOut, ExternalLink, Briefcase, FileText, Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api, { getApiUrl } from '../../utils/api';

const API_URL = getApiUrl();

const AdminLayout = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settings, setSettings] = useState({
    logo_url: '',
    logo_visible: true,
    site_name: 'BMS',
    site_name_full: 'BrightMind Software'
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/settings');
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/projects', icon: FolderKanban, label: 'Projeler' },
    { path: '/admin/services', icon: Briefcase, label: 'Hizmetler' },
    { path: '/admin/pages', icon: FileText, label: 'Sayfalar' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const logoUrl = settings.logo_url ? 
    (settings.logo_url.startsWith('http') ? settings.logo_url : `${API_URL}${settings.logo_url}`) : 
    null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-layout">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-2">
          {settings.logo_visible && logoUrl ? (
            <img src={logoUrl} alt={settings.site_name} className="h-8 w-auto" />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-bms-primary flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
          )}
          <span className="font-bold text-bms-text">{settings.site_name}</span>
        </Link>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </header>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-screen w-64 bg-white border-r border-gray-200 flex flex-col shadow-sm z-50
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        {/* Logo - Hidden on mobile (shown in header) */}
        <div className="hidden lg:block p-6 border-b border-gray-100">
          <Link to="/admin" className="flex items-center gap-3">
            {settings.logo_visible && logoUrl ? (
              <img src={logoUrl} alt={settings.site_name} className="h-10 w-auto object-contain" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-bms-primary flex items-center justify-center">
                <Brain className="w-6 h-6 text-white" />
              </div>
            )}
            <div>
              <span className="font-bold text-bms-text block">{settings.site_name}</span>
              <span className="text-bms-text-muted text-xs block">{settings.site_name_full}</span>
            </div>
          </Link>
        </div>

        {/* Mobile: Add top padding */}
        <div className="lg:hidden h-14" />

        {/* User Info */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-100">
          <span className="text-xs text-bms-text-muted">Giriş yapan:</span>
          <span className="text-sm font-medium text-bms-text block">{user?.username}</span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-1">
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive(item.path, item.exact)
                      ? 'bg-bms-primary text-white'
                      : 'text-bms-text-light hover:bg-gray-100 hover:text-bms-primary'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-green-600 hover:bg-green-50 transition-all"
          >
            <ExternalLink className="w-5 h-5" />
            Siteye Git
          </a>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-all"
          >
            <LogOut className="w-5 h-5" />
            Çıkış Yap
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        {/* Mobile: Add top padding for fixed header */}
        <div className="lg:hidden h-14" />
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
